import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'

import { PiWarning } from 'react-icons/pi'
import { NotFoundContent } from '../not-found-content'
import { useAggregationRefresh } from 'components/DataTable'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { queryClient } from 'services/queryClient'
import {
  getCampaignRefreshStatus,
  useGetCampaignById,
  useGetCampaignLeadReview,
} from 'features/campaigns/campaigns_api'
import { useAuth } from 'context/authentication/useAuth'
import {
  SequenceWizardSidebar,
  type WizardStep,
} from 'features/campaigns/SequenceWizardSidebar'
import { WizardSequenceStep } from 'features/campaigns/wizard-steps/WizardSequenceStep'
import { SequenceTemplatesDrawer } from 'features/campaigns/SequenceTemplatesDrawer'
import { SaveSequenceTemplateModal } from 'features/campaigns/SaveSequenceTemplateModal'
import {
  EMPTY_STEP_HISTORY,
  StepHistoryState,
} from 'features/campaigns/wizard-steps/stepHistory'
import { WizardSettingsStep } from 'features/campaigns/wizard-steps/WizardSettingsStep'
import { WizardReviewLeadsStep } from 'features/campaigns/wizard-steps/WizardReviewLeadsStep'
import { WizardLaunchStep } from 'features/campaigns/wizard-steps/WizardLaunchStep'
import { WizardReviewEmailsStep } from 'features/campaigns/wizard-steps/WizardReviewEmailsStep'
import { useGetCampaignSequence } from 'features/campaigns/sequences_api'
import {
  useGetCampaignLaunchReadiness,
  useGetCampaignSettings,
} from 'services/campaigns'
import { useEntityBreadcrumbs } from 'context/layout'
import { WORKFLOW_STATUS } from 'models/campaign'

const STEPS_READ_ONLY: WizardStep[] = ['sequence']
const STEPS_LIVE: WizardStep[] = [
  'review-leads',
  'sequence',
  'settings',
  'review-emails',
]
const STEPS_NO_CONTACTS: WizardStep[] = ['review-leads', 'sequence', 'settings']
const STEPS_WITH_CONTACTS: WizardStep[] = [
  'review-leads',
  'sequence',
  'settings',
  'review-emails',
  'launch',
]

export function SequenceWizardPageV2() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const hasBisonWorkspace = user?.company?.has_bison_workspace ?? false

  const [currentStep, setCurrentStep] = useState<WizardStep>('sequence')
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStep>>(
    new Set()
  )
  const [hasInitializedCurrentStep, setHasInitializedCurrentStep] =
    useState(false)
  // Build-step undo/redo history. Owned here (not by WizardSequenceStep) so
  // it survives switching to Settings/Review and back within a wizard visit;
  // it dies with the page on wizard exit.
  const [sequenceHistory, setSequenceHistory] =
    useState<StepHistoryState>(EMPTY_STEP_HISTORY)
  // Sequence templates (FIR-7633). Owned here so the drawer/modal overlay the
  // whole wizard (step rail + content), not just the build-step body.
  const [templatesOpen, setTemplatesOpen] = useState(false)
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false)
  // Bumped after a template is applied, to remount the build step so it
  // re-hydrates from the freshly written sequence (its hydration guard keys on
  // the sequence id, which is unchanged when a template overwrites an existing
  // sequence).
  const [sequenceStepResetKey, setSequenceStepResetKey] = useState(0)

  const campaignId = parseInt(id ?? '-1')
  const { data: campaign, isLoading } = useGetCampaignById(campaignId)
  const { data: sequence } = useGetCampaignSequence(campaignId, true)
  const { data: settings, isLoading: settingsLoading } = useGetCampaignSettings(
    campaignId,
    !!campaign
  )
  const { data: readiness, isLoading: readinessLoading } =
    useGetCampaignLaunchReadiness(campaignId, !isLoading)
  const { data: leadReview, isLoading: leadReviewLoading } =
    useGetCampaignLeadReview(campaignId)

  // Contact-request fulfillment is async on the backend (Celery). When
  // the company-wide campaigns refresh completes, new contacts may have
  // joined this campaign's recipient set — invalidate lead-review so the
  // wizard's recipient count picks them up without waiting for launch
  // (FIR-7242).
  useAggregationRefresh({
    queryKey: ['campaigns-refresh-status'],
    queryFn: getCampaignRefreshStatus,
    getIsRefreshing: (data) => data.is_refreshing,
    onRefetch: useCallback(() => {
      void queryClient.invalidateQueries({
        queryKey: ['campaign-lead-review', campaignId],
      })
    }, [campaignId]),
    enabled: campaignId > 0,
  })

  // Prefer live lead review count over the (potentially stale) campaigns list count
  const hasContacts =
    (leadReview?.summary.total ?? campaign?.contact_count ?? 0) > 0
  const campaignStatus = settings?.status || campaign?.workflow_status

  const isCompletedCampaign =
    campaign?.is_archived ||
    campaignStatus === 'completed' ||
    campaignStatus === 'archived' ||
    campaignStatus === 'deleted' ||
    campaign?.workflow_status === WORKFLOW_STATUS.COMPLETE

  const isActiveCampaign =
    !isCompletedCampaign &&
    (campaignStatus === 'launching' ||
      campaignStatus === 'queued' ||
      campaignStatus === 'active' ||
      campaign?.workflow_status === WORKFLOW_STATUS.ACTIVE)

  const isPausedCampaign =
    !isCompletedCampaign &&
    !isActiveCampaign &&
    (campaignStatus === 'paused' ||
      campaign?.workflow_status === WORKFLOW_STATUS.PAUSED)

  const isLiveCampaign = isActiveCampaign || isPausedCampaign

  const wizardTitle = isCompletedCampaign
    ? 'Sequence'
    : isActiveCampaign
      ? 'View Sequence'
      : isPausedCampaign
        ? 'Edit Sequence'
        : 'Sequence Wizard'

  const breadcrumbs = useMemo(
    () =>
      campaign
        ? [
            {
              label: campaign.name,
              path: `/campaigns/${id}/${isCompletedCampaign || isLiveCampaign ? 'dashboard' : 'detail'}`,
            },
            { label: wizardTitle },
          ]
        : [{ label: 'Campaigns', path: '/campaigns' }, { label: wizardTitle }],
    [campaign, id, isCompletedCampaign, isLiveCampaign, wizardTitle]
  )
  useEntityBreadcrumbs(breadcrumbs)
  const wizardSteps = useMemo(
    () =>
      isCompletedCampaign
        ? STEPS_READ_ONLY
        : isActiveCampaign || isPausedCampaign
          ? STEPS_LIVE
          : hasContacts
            ? STEPS_WITH_CONTACTS
            : STEPS_NO_CONTACTS,
    [hasContacts, isCompletedCampaign, isActiveCampaign, isPausedCampaign]
  )
  const settingsComplete =
    !!sequence &&
    (readiness?.sender_emails.is_complete ?? false) &&
    (readiness?.schedule.is_complete ?? false)

  const persistedCompletedSteps = useMemo(() => {
    const steps = new Set<WizardStep>()

    if (sequence) {
      if (hasContacts) {
        steps.add('review-leads')
      }
      steps.add('sequence')
    }

    if (settingsComplete) {
      steps.add('settings')
      // Review Emails is optional — always considered complete once settings are done
      steps.add('review-emails')
    }

    return steps
  }, [hasContacts, sequence, settingsComplete])

  const allCompletedSteps = useMemo(
    () =>
      new Set([
        ...Array.from(completedSteps),
        ...Array.from(persistedCompletedSteps),
      ]),
    [completedSteps, persistedCompletedSteps]
  )
  const accessibleSteps = useMemo(() => {
    if (isCompletedCampaign) {
      return new Set<WizardStep>(['sequence'])
    }

    if (isLiveCampaign) {
      return new Set<WizardStep>([
        'review-leads',
        'sequence',
        'settings',
        'review-emails',
      ])
    }

    const steps = new Set<WizardStep>(['review-leads', 'sequence'])

    if (sequence) {
      steps.add('settings')
    }

    if (sequence && hasContacts && readiness?.leads.is_complete) {
      steps.add('review-emails')
    }

    if (hasContacts && readiness?.all_complete) {
      steps.add('launch')
    }

    return steps
  }, [
    hasContacts,
    isCompletedCampaign,
    isLiveCampaign,
    readiness?.all_complete,
    readiness?.leads.is_complete,
    sequence,
  ])

  const recommendedStep: WizardStep = useMemo(() => {
    if (isCompletedCampaign || isLiveCampaign) {
      return 'sequence'
    }
    if (!sequence) {
      return 'review-leads'
    }
    if (readiness?.all_complete) {
      return 'launch'
    }
    return 'settings'
  }, [isCompletedCampaign, isLiveCampaign, readiness?.all_complete, sequence])

  useEffect(() => {
    setCurrentStep('sequence')
    setCompletedSteps(new Set())
    setHasInitializedCurrentStep(false)
  }, [campaignId])

  useEffect(() => {
    if (isLoading || readinessLoading || !campaign || isCompletedCampaign) {
      return
    }

    if (!hasInitializedCurrentStep) {
      setCurrentStep(recommendedStep)
      setHasInitializedCurrentStep(true)
      return
    }

    if (
      !wizardSteps.includes(currentStep) ||
      (currentStep === 'launch' && !readiness?.all_complete)
    ) {
      setCurrentStep(recommendedStep)
    }
  }, [
    campaign,
    currentStep,
    hasInitializedCurrentStep,
    isCompletedCampaign,
    isLoading,
    readiness?.all_complete,
    readinessLoading,
    recommendedStep,
    wizardSteps,
  ])

  if (isLoading || readinessLoading || settingsLoading || leadReviewLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner animation="border" />
      </div>
    )
  }

  if (!campaign) {
    return <NotFoundContent />
  }

  const resolvedCurrentStep = isCompletedCampaign
    ? 'sequence'
    : wizardSteps.includes(currentStep)
      ? currentStep
      : recommendedStep

  const currentStepIndex = wizardSteps.indexOf(resolvedCurrentStep)
  const isLastStep = currentStepIndex === wizardSteps.length - 1

  const goToNext = () => {
    if (!isLastStep) {
      setCompletedSteps((prev) => new Set([...prev, resolvedCurrentStep]))
      setCurrentStep(wizardSteps[currentStepIndex + 1])
    }
  }

  const goToPrev = () => {
    if (currentStepIndex === 0) {
      navigate(
        `/campaigns/${id}/${isCompletedCampaign || isLiveCampaign ? 'dashboard' : 'detail'}`
      )
      return
    }
    setCurrentStep(wizardSteps[currentStepIndex - 1])
  }

  const handleStepClick = (step: WizardStep) => {
    if (step === 'launch') {
      if (hasContacts && readiness?.all_complete) {
        setCurrentStep(step)
      }
      return
    }

    setCurrentStep(step)
  }

  return (
    <Ds2026ButtonProvider>
      <div className="relative flex flex-1 overflow-hidden">
        <SequenceWizardSidebar
          currentStep={resolvedCurrentStep}
          completedSteps={allCompletedSteps}
          accessibleSteps={accessibleSteps}
          campaign={campaign}
          campaignUrl={`/campaigns/${id}/${isCompletedCampaign || isLiveCampaign ? 'dashboard' : 'detail'}`}
          hasContacts={hasContacts}
          onStepClick={handleStepClick}
          visibleStepsOverride={wizardSteps}
          title={wizardTitle}
          recipientSummary={leadReview?.summary}
        />

        <main className="flex flex-1 flex-col overflow-auto rounded-tr-2xl bg-surface-bright">
          {!hasBisonWorkspace ? (
            <div className="flex flex-1 items-center justify-center p-8">
              <div className="flex max-w-md flex-col items-center gap-4 rounded-lg border border-line bg-surface p-8 text-center">
                <PiWarning className="size-10 text-warning" />
                <h3 className="text-lg font-semibold text-fg">
                  Email Outreach Not Configured
                </h3>
                <p className="text-sm text-fg-secondary">
                  Your company needs an email outreach workspace before you can
                  build sequences. Please contact your admin or set up outreach
                  in Settings &gt; Email Outreach.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(`/campaigns/${id}/detail`)}
                  className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90"
                >
                  Back to Campaign
                </button>
              </div>
            </div>
          ) : resolvedCurrentStep === 'sequence' ? (
            <WizardSequenceStep
              key={sequenceStepResetKey}
              campaignId={campaign.id}
              campaignName={campaign.name}
              bisonCampaignId={sequence?.bison_campaign_id}
              onBack={goToPrev}
              onNext={goToNext}
              onOpenTemplates={() => setTemplatesOpen(true)}
              onOpenSaveTemplate={() => setSaveTemplateOpen(true)}
              stepNumber={wizardSteps.indexOf('sequence') + 1}
              totalSteps={wizardSteps.length}
              readOnly={isCompletedCampaign || isActiveCampaign}
              isLiveCampaign={isPausedCampaign}
              isActiveCampaign={isActiveCampaign}
              backLabel={
                isCompletedCampaign || isLiveCampaign
                  ? 'Back to Dashboard'
                  : 'Back'
              }
              history={sequenceHistory}
              onHistoryChange={setSequenceHistory}
            />
          ) : resolvedCurrentStep === 'settings' ? (
            <WizardSettingsStep
              campaignId={campaign.id}
              onNext={goToNext}
              onBack={goToPrev}
              hasContacts={hasContacts}
              stepNumber={wizardSteps.indexOf('settings') + 1}
              totalSteps={wizardSteps.length}
              onSaveExit={() =>
                navigate(
                  `/campaigns/${id}/${isLiveCampaign ? 'dashboard' : 'detail'}`
                )
              }
              isLiveCampaign={isPausedCampaign}
              readOnly={isActiveCampaign}
              campaignStatus={campaignStatus}
            />
          ) : resolvedCurrentStep === 'review-leads' ? (
            <WizardReviewLeadsStep
              campaignId={campaign.id}
              bisonCampaignId={sequence?.bison_campaign_id ?? 0}
              onBack={goToPrev}
              onNext={goToNext}
              stepNumber={wizardSteps.indexOf('review-leads') + 1}
              totalSteps={wizardSteps.length}
              isLiveCampaign={isLiveCampaign}
              campaign={campaign}
            />
          ) : resolvedCurrentStep === 'review-emails' ? (
            <WizardReviewEmailsStep
              campaignId={campaign.id}
              bisonCampaignId={sequence?.bison_campaign_id ?? 0}
              onBack={goToPrev}
              onNext={
                isLiveCampaign
                  ? () => navigate(`/campaigns/${id}/dashboard`)
                  : goToNext
              }
              stepNumber={wizardSteps.indexOf('review-emails') + 1}
              totalSteps={wizardSteps.length}
              isLiveCampaign={isPausedCampaign}
              readOnly={isActiveCampaign}
            />
          ) : resolvedCurrentStep === 'launch' ? (
            <WizardLaunchStep
              campaignId={campaign.id}
              campaign={campaign}
              onBack={goToPrev}
              onLaunch={() => navigate(`/campaigns/${id}/dashboard`)}
              stepNumber={wizardSteps.indexOf('launch') + 1}
              totalSteps={wizardSteps.length}
            />
          ) : null}
        </main>

        {/* Mounted at the wizard-container level so the overlay covers the
            step rail too (not just the build-step body), while still leaving
            the global app nav reachable. */}
        <SequenceTemplatesDrawer
          open={templatesOpen}
          onClose={() => setTemplatesOpen(false)}
          campaignId={campaign.id}
          onApplied={() => setSequenceStepResetKey((k) => k + 1)}
        />
        <SaveSequenceTemplateModal
          open={saveTemplateOpen}
          onClose={() => setSaveTemplateOpen(false)}
          campaignId={campaign.id}
          stepCount={sequence?.steps.length ?? 0}
        />
      </div>
    </Ds2026ButtonProvider>
  )
}
